import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Stack,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CreditCard as CreditCardIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks';
import api from '../../services/api';
import { grey, success, warning } from '../../styles/tokens';
import SubscriptionDialog from '../../components/common/SubscriptionDialog';

const EditProfile = () => {
  const { user, logout, refreshAuthToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);

  // Determine if user is coach or admin
  const isCoach = user?.role === 'coach';
  const baseRoute = isCoach ? '/coach/auth' : '/admin/auth';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Refresh token first
        try {
          await api.post(`${baseRoute}/refresh-token`, {});
        } catch (refreshErr) {
          console.error('Token refresh failed:', refreshErr);
          // Continue anyway to try fetching profile
        }

        // Fetch profile
        const data = await api.get(`${baseRoute}/profile`);

        setProfile({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          password: '',
          confirmPassword: '',
        });
        setIsSubscribed(data.isSubscribed);
        setSubscriptionStartDate(data.subscriptionStartDate);
        setCancelAtPeriodEnd(data.cancelAtPeriodEnd || false);
        setCurrentPeriodEnd(data.currentPeriodEnd);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Impossible de charger le profil.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [baseRoute]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');

    if (profile.password && !validatePassword(profile.password)) {
      setError(
        'Le mot de passe doit contenir au moins 8 caracteres, une majuscule, un chiffre et un caractere special.'
      );
      setSaving(false);
      return;
    }

    if (profile.password !== profile.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      setSaving(false);
      return;
    }

    try {
      await api.put(`${baseRoute}/profile`, profile);
      setSuccessMessage('Profil mis a jour avec succes.');
      setProfile(prev => ({ ...prev, password: '', confirmPassword: '' }));
      // Rafraîchir le token pour mettre à jour le contexte avec les nouvelles données
      await refreshAuthToken();
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise a jour du profil.');
    } finally {
      setSaving(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const data = await api.post(`${baseRoute}/customer-portal`, {});
      console.log('Customer portal response:', data);

      // Si redirect demande (plan free sans stripeCustomerId)
      if (data.redirect) {
        console.log('Redirecting to:', data.redirectUrl);
        navigate(data.redirectUrl);
        return;
      }

      // Sinon, ouvrir le portal Stripe
      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No URL in response:', data);
        setError('Erreur: pas d\'URL de redirection.');
      }
    } catch (err) {
      console.error('Error managing subscription:', err);
      setError('Erreur lors de la gestion de l\'abonnement.');
    }
  };

  const handleSubscribe = () => {
    setSubscriptionDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Profil - {isCoach ? 'Coach' : 'Admin'} Yoco</title>
      </Helmet>

      <Box sx={{ maxWidth: 480 }}>
        {/* Header */}
        <Typography variant="h1" sx={{ mb: 3 }}>
          Profil
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {/* Subscription Section - Coach only */}
        {isCoach && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <CreditCardIcon sx={{ fontSize: 20, color: grey[500] }} />
                  <Typography variant="h4">
                    Abonnement
                  </Typography>
                </Stack>
                <Chip
                  label={cancelAtPeriodEnd ? 'Annulé' : (isSubscribed ? 'Actif' : 'Inactif')}
                  size="small"
                  sx={{
                    bgcolor: cancelAtPeriodEnd ? warning[50] : (isSubscribed ? success[50] : grey[100]),
                    color: cancelAtPeriodEnd ? warning.dark : (isSubscribed ? success.dark : grey[600]),
                  }}
                />
              </Stack>

              {/* Afficher le message d'annulation avec date de fin */}
              {cancelAtPeriodEnd && currentPeriodEnd && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2, py: 0.5 }}
                  icon={false}
                >
                  <Typography variant="body2">
                    Votre abonnement a ete annule. Il restera actif jusqu'au{' '}
                    <strong>{new Date(currentPeriodEnd).toLocaleDateString('fr-FR')}</strong>.
                  </Typography>
                </Alert>
              )}

              {/* Afficher la date de debut si actif et pas annule */}
              {isSubscribed && subscriptionStartDate && !cancelAtPeriodEnd && (
                <Typography variant="caption" sx={{ display: 'block', mb: 2, color: grey[500] }}>
                  Actif depuis le {new Date(subscriptionStartDate).toLocaleDateString('fr-FR')}
                </Typography>
              )}

              <Button
                variant={isSubscribed ? 'outlined' : 'contained'}
                fullWidth
                size="small"
                onClick={isSubscribed ? handleManageSubscription : handleSubscribe}
              >
                {cancelAtPeriodEnd ? 'Reactiver l\'abonnement' : (isSubscribed ? 'Gerer l\'abonnement' : 'S\'abonner')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Profile Form */}
        <Card>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 2.5 }}>
              Informations personnelles
            </Typography>

            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Prenom"
                    name="firstName"
                    value={profile.firstName}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Nom"
                    name="lastName"
                    value={profile.lastName}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                  />
                </Stack>

                <TextField
                  label="Email"
                  name="email"
                  value={profile.email}
                  disabled
                  fullWidth
                  size="small"
                />

                <TextField
                  label="Telephone"
                  name="phone"
                  value={profile.phone}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                />

                <Divider sx={{ my: 1 }} />

                <Typography variant="caption">
                  Laisser vide pour conserver le mot de passe actuel
                </Typography>

                <TextField
                  label="Nouveau mot de passe"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={profile.password}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirmer le mot de passe"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={profile.confirmPassword}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={20} color="inherit" /> : 'Mettre a jour'}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>

        {/* Logout Button - Mobile only */}
        {isMobile && (
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={logout}
            sx={{
              mt: 3,
              py: 1.5,
              color: grey[600],
              borderColor: grey[300],
              '&:hover': {
                bgcolor: grey[50],
                borderColor: grey[400],
              },
            }}
          >
            Se deconnecter
          </Button>
        )}
      </Box>

      {/* Subscription Dialog */}
      <SubscriptionDialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
      />
    </>
  );
};

export default EditProfile;
